import { queryOptions, useQuery } from "@tanstack/react-query";
import { API_HOST } from "../../App";
import { z } from "zod";

const TableSchema = z.object({
  name: z.string(),
  tableIdentifier: z.string(),
  id: z.string(),
});

const tablesResponse = z.array(TableSchema);

export const tablesQueryOptions = (locationId: string, enabled: boolean) =>
  queryOptions({
    queryKey: ["tables", locationId],
    queryFn: async () => {
      const response = await fetch(`${API_HOST}/v1/tables/${locationId}/`);
      const data = await response.json();
      const parsed = tablesResponse.parse(data).map((table) => ({
        ...table,
        tableIdentifier: table.tableIdentifier.trim(),
      }));
      return parsed.sort((a, b) => {
        return a.tableIdentifier.localeCompare(b.tableIdentifier, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
    },
    enabled,
  });

export const useTables = (locationId: string, enabled = false) => {
  return useQuery(tablesQueryOptions(locationId, enabled));
};

export const tableOpenQueryOptions = (locationId: string, table?: string) =>
  queryOptions({
    queryKey: ["tableOpen", locationId, table],
    refetchInterval: 15_000,
    queryFn: async () => {
      if (!table) {
        return { isOpen: false };
      }
      const response = await fetch(
        `${API_HOST}/v1/tables/${locationId}/chequecheck/${table}`
      );
      const data = await response.json();

      const isOpenSchema = z.object({
        isOpen: z.boolean(),
      });

      const parsed = isOpenSchema.parse(data);

      return parsed;
    },
  });

export const useTableOpen = (locationId: string, table?: string) => {
  return useQuery(tableOpenQueryOptions(locationId, table));
};

export const useTableExists = (locationId: string, tableId?: string) => {
  if (!tableId) {
    return false;
  }

  const { data: tables } = useTables(locationId, true);
  if (!tables) {
    return false;
  }
  return tables.some((table) => table.id === tableId);
};
